import { InView } from 'react-intersection-observer'
import { Picture } from 'src/components/ui/Image'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'

interface InlineVerticalBannerProps {
  bannerUrlMobile: string
  bannerUrlDesktop: string
  imageDescription: string
  linkUrl?: string
  title?: string
  showButton?: boolean
  buttonText?: string
  bannerName?: string
}
export function InlineVerticalBanner({
  bannerUrlMobile,
  bannerUrlDesktop,
  imageDescription,
  linkUrl,
  title,
  showButton = false,
  buttonText,
  bannerName,
}: InlineVerticalBannerProps) {
  const trimmedLinkUrl = linkUrl?.trim()
  const trimmedTitle = title?.trim()
  const hasLink = Boolean(trimmedLinkUrl)
  const hasTitle = Boolean(trimmedTitle)
  const shouldShowButton = Boolean(showButton && buttonText?.trim() && hasLink)
  const shouldShowTopOverlay = hasTitle
  const shouldShowBottomOverlay = shouldShowButton

  const handleViewed = () => {
    dispatchAmplitudeEvent({
      eventName: 'Promo Feature Viewed',
      eventData: {
        type: 'Contextual',
        'promotional element name': bannerName ?? 'Banner Inline Vertical',
      },
    })
  }

  const handleClick = () => {
    if (!bannerName) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        type: 'Contextual',
        'promotional element name': bannerName,
      },
    })
  }

  return (
    <InView
      as="section"
      triggerOnce
      onChange={(inView) => {
        if (inView) {
          handleViewed()
        }
      }}
    >
      <div className="inline-vertical-banner relative max-h-[344px] w-full overflow-hidden bg-transparent md:h-[486px] md:max-h-[486px]">
        {hasLink && (
          <a
            href={trimmedLinkUrl}
            onClick={handleClick}
            className="absolute inset-0 z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
            aria-label={imageDescription}
          >
            <span className="sr-only">{imageDescription}</span>
          </a>
        )}

        <Picture
          sources={[
            {
              src: bannerUrlMobile,
              width: 171,
              height: 344,
              media: '(max-width: 767px)',
            },
          ]}
          img={{
            src: bannerUrlDesktop,
            width: 402,
            height: 486,
            alt: imageDescription,
            loading: 'lazy',
            decoding: 'async',
            className: 'h-full w-full object-cover object-center',
          }}
          shouldCompress={false}
        />

        {shouldShowTopOverlay && (
          <div className="pointer-events-none absolute inset-x-0 top-[146px] h-[99px] bg-gradient-to-b from-transparent to-black/45 md:top-auto md:bottom-[172px] md:h-[173px] md:to-black/35" />
        )}

        {shouldShowBottomOverlay && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[99px] bg-gradient-to-b from-black/20 to-black/70 md:h-[172px] md:from-black/15 md:to-black/55" />
        )}

        <div className="absolute inset-x-[16px] bottom-[11px] z-20 flex flex-col items-center gap-[12px] md:inset-x-5 md:bottom-[22px] md:gap-[18px]">
          {hasTitle && (
            <p className="m-0 text-center text-[11px] leading-[16.5px] font-semibold tracking-[2.64px] uppercase text-white md:text-[14px] md:leading-[16px] md:tracking-normal">
              {trimmedTitle}
            </p>
          )}

          {shouldShowButton && (
            <a
              href={trimmedLinkUrl}
              onClick={handleClick}
              className="inline-flex h-[42px] w-full items-center justify-center rounded-[360px] bg-white px-[10px] py-1 text-center text-[13px] leading-[19.5px] font-semibold text-[#222222] shadow-[0px_2px_4px_rgba(0,0,0,0.18)] md:h-[64px] md:px-4 md:text-[18px] md:leading-[27px] md:text-[#4A3B2C] md:shadow-none"
            >
              {buttonText}
            </a>
          )}
        </div>
      </div>
    </InView>
  )
}
